import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, PenLine, QrCode, Smartphone, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { errorMessage, orgVippsRequest, orgVippsWithdraw } from "@/lib/api";
import { fmtDateTime } from "@/lib/tickets";
import { cn } from "@/lib/utils";

const LINK_RE = /^https:\/\/qr\.vipps\.no\/\S+$/;

const VippsForm = ({ token, vipps, onDone, onCancel }) => {
  const qc = useQueryClient();
  const [number, setNumber] = useState(vipps.number || "");
  const [link, setLink] = useState(vipps.link || "");
  const [note, setNote] = useState("");
  const m = useMutation({
    mutationFn: () => orgVippsRequest(token, { vipps_number: number, vipps_link: link.trim() || null, note: note.trim() || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["org", token] }); toast.success("Sendt til styret – dei stadfestar før det vert teke i bruk"); onDone(); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const digits = (() => { const d = number.replace(/[\s.+-]/g, ""); return d.length === 10 && d.startsWith("47") ? d.slice(2) : d; })();
  const numberOk = /^(\d{1,6}|\d{8})$/.test(digits);
  const linkOk = !link.trim() || LINK_RE.test(link.trim());
  const unchanged = digits === (vipps.number || "") && (link.trim() || null) === (vipps.link || null);
  return (
    <form className="mt-4 space-y-4" onSubmit={(e) => { e.preventDefault(); if (numberOk && linkOk) m.mutate(); }} data-testid="vipps-form">
      <label className="block">
        <span className="text-[13px] font-medium">Vippsnummer</span>
        <Input value={number} onChange={(e) => setNumber(e.target.value)} inputMode="numeric" placeholder="t.d. 12345" className="mt-1 h-11 rounded-[10px] bg-background text-[16px] tabular" data-testid="vipps-number-input" />
        <span className={cn("mt-1 block text-[12.5px]", number && !numberOk ? "text-status-cancelled" : "text-muted-foreground")}>Vippsnummeret til laget (1–6 siffer) eller eit mobilnummer. Kjøparane betaler hit.</span>
      </label>
      <label className="block">
        <span className="text-[13px] font-medium">Vipps-lenkje <span className="font-normal text-muted-foreground">(valfritt, men gjev «Opne Vipps»-knapp og QR)</span></span>
        <Input value={link} onChange={(e) => setLink(e.target.value)} inputMode="url" placeholder="https://qr.vipps.no/…" className="mt-1 h-11 rounded-[10px] bg-background text-[14px]" data-testid="vipps-link-input" />
        <span className={cn("mt-1 block text-[12.5px] leading-relaxed", link && !linkOk ? "text-status-cancelled" : "text-muted-foreground")}>
          {link && !linkOk ? "Lenkja må byrje med https://qr.vipps.no/" : "Finn ho i Vipps-appen under Vippsnummeret dykkar (Del QR-kode / lenkje) eller i portal.vippsmobilepay.com → QR-kodar. Test lenkja på mobilen før du sender."}
        </span>
      </label>
      <label className="block">
        <span className="text-[13px] font-medium">Melding til styret <span className="font-normal text-muted-foreground">(valfritt)</span></span>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="t.d. nytt Vippsnummer etter årsmøtet" className="mt-1 rounded-[10px] bg-background" data-testid="vipps-note-input" />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="h-11 rounded-[10px]" disabled={m.isPending || !numberOk || !linkOk || unchanged} data-testid="vipps-submit-button">{m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send til styret"}</Button>
        <Button type="button" variant="ghost" className="h-11" onClick={onCancel} data-testid="vipps-cancel-button">Avbryt</Button>
        {unchanged && numberOk && <span className="self-center text-[12.5px] text-muted-foreground" data-testid="vipps-unchanged-hint">Ingenting er endra</span>}
      </div>
    </form>
  );
};

/** On the organizer's front page: where ticket money goes today, and the way to ask the board for a change. */
export const VippsCard = ({ token, vipps, needsVipps = false }) => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const withdraw = useMutation({
    mutationFn: () => orgVippsWithdraw(token),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["org", token] }); toast.success("Førespurnaden er trekt"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!vipps) return null;
  const req = vipps.request;
  const pending = req?.status === "pending";
  const missing = !vipps.number && needsVipps;
  return (
    <section className={cn("mt-8 rounded-[20px] border p-5 sm:p-6", missing ? "verdict-warn" : "border-border bg-card")} data-testid="organizer-vipps-card" data-state={pending ? "pending" : vipps.number ? "set" : "missing"}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary"><Smartphone className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="t-mono text-muted-foreground">Billettpengane går til</p>
            <p className="mt-1 font-display text-[24px] font-semibold leading-none tracking-[-0.02em] tabular" data-testid="organizer-vipps-number">{vipps.number ? `Vipps ${vipps.number}` : "Ikkje registrert"}</p>
            <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground" data-testid="organizer-vipps-hint">
              {vipps.link ? <span className="inline-flex items-center gap-1.5"><QrCode className="h-3.5 w-3.5" /> Kjøparane får «Opne Vipps»-knapp og QR frå lenkja dykkar.</span>
                : vipps.number ? "Kjøparane får nummeret og ein referansekode. Legg til Vipps-lenkja dykkar, så får dei ein knapp som opnar Vipps ferdig utfylt."
                : missing ? "Arrangementa dykkar har Vipps-betaling, men styret har ikkje registrert noko Vippsnummer. Send det inn her." : "Styret må registrere eit Vippsnummer før de kan selje billettar med Vipps."}
            </p>
          </div>
        </div>
        {!pending && !editing && <Button variant={missing ? "default" : "outline"} className={cn("h-10 shrink-0 rounded-[10px]", !missing && "bg-transparent")} onClick={() => setEditing(true)} data-testid="organizer-vipps-edit-button"><PenLine className="h-4 w-4" /> {vipps.number ? "Endre" : "Legg inn"}</Button>}
      </div>

      {pending && (
        <div className="mt-4 flex flex-col gap-3 rounded-[14px] bg-[hsl(var(--status-pending-bg))] px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between" data-testid="organizer-vipps-pending">
          <div className="text-[14px]">
            <p className="font-medium"><span className="live-dot mr-2 align-middle" aria-hidden="true" />Ventar på styret: Vipps {req.vipps_number}{req.vipps_link ? " · med lenkje" : ""}</p>
            <p className="mt-0.5 text-[12.5px] text-muted-foreground">Sendt {fmtDateTime(req.requested_at)}. Noverande oppsett gjeld til styret har svart.</p>
          </div>
          <Button variant="ghost" size="sm" className="h-9 shrink-0" disabled={withdraw.isPending} onClick={() => withdraw.mutate()} data-testid="organizer-vipps-withdraw-button"><Undo2 className="h-4 w-4" /> Trekk tilbake</Button>
        </div>
      )}
      {!pending && req?.status === "rejected" && !editing && (
        <p className="mt-4 rounded-[14px] border border-border bg-background px-4 py-3 text-[13.5px]" data-testid="organizer-vipps-rejected">Styret godkjende ikkje Vipps {req.vipps_number}{req.board_note ? <>: <span className="text-muted-foreground">«{req.board_note}»</span></> : "."} Du kan sende ein ny førespurnad.</p>
      )}
      {editing && <VippsForm token={token} vipps={vipps} onDone={() => setEditing(false)} onCancel={() => setEditing(false)} />}
    </section>
  );
};
